import { clamp, clamp01, lerp } from '../core/math';
import type { AiSnapshot } from '../ai/boxer';
import type { PunchType } from '../recog/events';
import type { IncomingAttack } from '../sim/types';

/**
 * OPPONENT RENDERING — readability first
 * ======================================
 *
 * The opponent's entire perceptual job is to answer four questions instantly:
 * where are they, what is their guard doing, WHICH PUNCH IS COMING, and how far
 * into the wind-up is it. The third one is the game.
 *
 * The previous version failed at it completely. Its wind-up pose was identical
 * for every punch type — the only differentiation was a lateral offset applied
 * at the *end* of the strike, i.e. after the player had already been forced to
 * commit. Straights and uppercuts were pixel-identical. And no arms were drawn,
 * only floating gloves, which removes the single strongest cue a human uses to
 * read a punch: the elbow.
 *
 * What games actually do, and what is applied here:
 *
 * 1. SILHOUETTE. Each attack has an outline you could read as a black shape.
 *    Hooks put the elbow high and wide; uppercuts drop the fist below the ribs;
 *    overhands lift it above the head. The arm is drawn as shoulder-elbow-fist
 *    because the elbow carries most of the information.
 *
 * 2. HELD ANTICIPATION. Punch-Out and every fighting game reach the readable
 *    pose fast and then HOLD it, rather than smoothly interpolating through it.
 *    Smooth interpolation means the distinctive frame exists for one instant at
 *    an unpredictable moment. Here: 0-28% move to the pose, 28-62% hold it (this
 *    is the read window), 62-100% snap.
 *
 * 3. ARCS. A hook travels a visible curve, an uppercut comes from below. The
 *    path is a quadratic Bezier with a per-type control point, so the trajectory
 *    itself is diagnostic even before the fist arrives.
 *
 * 4. COLOUR CODING. Consistent per type. This is the single highest-value
 *    readability change and it costs nothing. It is deliberately paired with
 *    distinct silhouettes and arcs so it is not the *only* cue — colour alone
 *    would exclude colour-blind players.
 *
 * 5. MOTION TRAIL. Sampled analytically from the path function at earlier
 *    progress values, so it costs no state and cannot desync from the fist.
 *
 * 6. BODY REACTION. Torso rotation for a cross, shoulder dip for a hook, a dip
 *    of the whole rig for an uppercut. Real punches are read from the body
 *    before the hand moves.
 */

export interface OpponentDrawState {
  ai: AiSnapshot;
  gap: number;
  lateral: number;
  telegraph: IncomingAttack | null;
  now: number;
  /** 0..1 flash when the opponent has just been hit. */
  hitFlash: number;
}

const SKIN = '#c98d63';
const GLOVE = '#d8352f';
const GLOVE_DARK = '#8d1f1c';

/** Per-type colour. Learnable, and consistent with the HUD threat bar. */
const PUNCH_COLOUR: Record<PunchType, string> = {
  jab: '#7fd8ff',
  cross: '#7fd8ff',
  hook: '#ffb43c',
  uppercut: '#c98bff',
  overhand: '#ff8a5a',
};

/** Animation phase boundaries. The hold is what makes the pose readable. */
export const WIND_IN = 0.28;
export const HOLD_END = 0.62;

/** How the torso participates in the tell. `dx` is multiplied by the punch side. */
const BODY_REACTION: Record<PunchType, { dx: number; dip: number }> = {
  jab: { dx: -8, dip: 0 },
  cross: { dx: -8, dip: 0 },
  hook: { dx: 9, dip: 4 },
  uppercut: { dx: -3, dip: 14 },
  overhand: { dx: 7, dip: -6 },
};

interface Pt {
  x: number;
  y: number;
}

export interface Geom {
  scale: number;
  shoulderY: number;
  headY: number;
  hipY: number;
  torsoH: number;
}

/** Where the arm sits for each phase of each punch, in rig-local units. */
interface ArmKey {
  wind: { elbow: Pt; fist: Pt };
  strike: { elbow: Pt; fist: Pt };
  /** Bezier control point for the fist's travel. Off the straight line = an arc. */
  control: Pt;
  /** Glove growth at impact. High = coming straight at the camera. */
  gloveScale: number;
}

export function armKeys(type: PunchType, dir: number, g: Geom, targetY: number): ArmKey {
  const { shoulderY, headY, hipY } = g;
  switch (type) {
    case 'hook':
      // Elbow up to shoulder height and flared wide — a horizontal L off the
      // side of the body. The most distinctive silhouette of the four.
      return {
        wind: { elbow: { x: dir * 80, y: shoulderY + 2 }, fist: { x: dir * 76, y: headY + 4 } },
        strike: { elbow: { x: dir * 30, y: shoulderY + 12 }, fist: { x: -dir * 20, y: targetY } },
        control: { x: dir * 96, y: targetY - 26 },
        gloveScale: 1.9,
      };
    case 'uppercut':
      // Fist drops below the ribs, arm hangs. Whole rig dips to load the legs.
      return {
        wind: { elbow: { x: dir * 52, y: shoulderY + 98 }, fist: { x: dir * 32, y: hipY - 6 } },
        strike: { elbow: { x: dir * 34, y: shoulderY + 58 }, fist: { x: dir * 10, y: targetY - 8 } },
        control: { x: dir * 24, y: hipY + 16 },
        gloveScale: 2.2,
      };
    case 'overhand':
      // Fist lifts above the head, elbow high and back. Comes over the top.
      return {
        wind: { elbow: { x: dir * 70, y: shoulderY - 44 }, fist: { x: dir * 58, y: headY - 56 } },
        strike: { elbow: { x: dir * 30, y: shoulderY + 8 }, fist: { x: dir * 6, y: targetY } },
        control: { x: dir * 42, y: headY - 64 },
        gloveScale: 2.4,
      };
    default:
      // Straight: the hand cocks back and the shoulder loads. Minimal lateral
      // travel, maximum perspective growth — it arrives down the pipe.
      return {
        wind: { elbow: { x: dir * 40, y: shoulderY + 72 }, fist: { x: dir * 24, y: headY + 16 } },
        strike: { elbow: { x: dir * 22, y: lerp(shoulderY, targetY, 0.45) }, fist: { x: dir * 8, y: targetY } },
        control: { x: dir * 26, y: lerp(headY, targetY, 0.5) },
        gloveScale: 2.7,
      };
  }
}

function bezier(a: Pt, c: Pt, b: Pt, t: number): Pt {
  const u = 1 - t;
  return {
    x: u * u * a.x + 2 * u * t * c.x + t * t * b.x,
    y: u * u * a.y + 2 * u * t * c.y + t * t * b.y,
  };
}

/** Arm pose at a given telegraph progress. Pure, so the trail is free. */
function armAt(
  p: number,
  k: ArmKey,
  rest: { elbow: Pt; fist: Pt },
): { elbow: Pt; fist: Pt; gloveScale: number; strikeT: number } {
  if (p <= WIND_IN) {
    // Snap into the readable pose quickly — ease-out, not linear.
    const t = clamp01(p / WIND_IN);
    const e = 1 - (1 - t) * (1 - t);
    return {
      elbow: { x: lerp(rest.elbow.x, k.wind.elbow.x, e), y: lerp(rest.elbow.y, k.wind.elbow.y, e) },
      fist: { x: lerp(rest.fist.x, k.wind.fist.x, e), y: lerp(rest.fist.y, k.wind.fist.y, e) },
      gloveScale: 1,
      strikeT: 0,
    };
  }
  if (p <= HOLD_END) {
    // HOLD. This is the read window. A tiny tremor keeps it from looking frozen
    // without smearing the silhouette.
    return { elbow: k.wind.elbow, fist: k.wind.fist, gloveScale: 1, strikeT: 0 };
  }
  // Strike: accelerate into the target.
  const t = clamp01((p - HOLD_END) / (1 - HOLD_END));
  const e = t * t;
  return {
    elbow: { x: lerp(k.wind.elbow.x, k.strike.elbow.x, e), y: lerp(k.wind.elbow.y, k.strike.elbow.y, e) },
    fist: bezier(k.wind.fist, k.control, k.strike.fist, e),
    gloveScale: 1 + (k.gloveScale - 1) * e,
    strikeT: t,
  };
}

export function drawOpponent(ctx: CanvasRenderingContext2D, w: number, h: number, s: OpponentDrawState): void {
  const { ai, gap, lateral } = s;

  const refGap = 1.32;
  const scale = clamp((refGap / Math.max(0.35, gap)) * (h / 620), 0.35, 3.2);
  const cx = w * 0.5 - lateral * 120 * scale + ai.ring.x * 40 * scale;
  const groundY = h * 0.92;

  let headDx = 0;
  let headDy = 0;
  let bodyDx = 0;
  switch (ai.posture) {
    case 'slip-left':
      headDx = -26;
      bodyDx = -8;
      break;
    case 'slip-right':
      headDx = 26;
      bodyDx = 8;
      break;
    case 'duck':
      headDy = 42;
      break;
    case 'roll':
      headDx = -18;
      headDy = 30;
      bodyDx = -6;
      break;
    case 'pull':
      headDy = -6;
      break;
    default:
      break;
  }

  const energy = 0.35 + 0.65 * clamp01(ai.vitals.stamina / 100);
  const bob = Math.sin(s.now / (260 / energy)) * 4 * energy;

  const tel = ai.telegraphing;
  const colour = tel ? PUNCH_COLOUR[tel.type] : null;

  // Body reaction, applied before anything is positioned so the whole rig
  // participates in the tell rather than just the arm. Real punches are read
  // from the torso before the hand has gone anywhere.
  let dip = 0;
  if (tel) {
    const b = BODY_REACTION[tel.type];
    const dir = tel.side === 'left' ? -1 : 1;
    const load = tel.progress <= HOLD_END ? clamp01(tel.progress / WIND_IN) : 1 - clamp01((tel.progress - HOLD_END) / 0.3);
    bodyDx += b.dx * dir * load;
    dip = b.dip * load;
  }

  const torsoH = 150 * scale;
  const torsoW = 92 * scale;
  const headR = 30 * scale;
  const hipY = groundY - 150 * scale + bob + dip * scale;
  const shoulderY = hipY - torsoH;
  const headY = shoulderY - headR * 1.25 + headDy * scale;

  const geom: Geom = { scale, shoulderY, headY, hipY, torsoH };

  ctx.save();
  ctx.translate(cx, 0);

  // Ground shadow.
  ctx.globalAlpha = 0.35;
  ctx.fillStyle = '#000';
  ctx.beginPath();
  ctx.ellipse(0, groundY + 6, 70 * scale, 16 * scale, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;

  // Legs
  ctx.strokeStyle = '#1c2230';
  ctx.lineWidth = 22 * scale;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-18 * scale, hipY);
  ctx.lineTo(-30 * scale, groundY - 4);
  ctx.moveTo(18 * scale, hipY);
  ctx.lineTo(34 * scale, groundY - 4);
  ctx.stroke();

  // Torso
  ctx.fillStyle = ai.vitals.poise < 30 ? '#7a3a3a' : '#2a3446';
  roundRect(ctx, -torsoW / 2 + bodyDx * scale, shoulderY, torsoW, torsoH, 22 * scale);
  ctx.fill();

  // Head
  ctx.fillStyle = SKIN;
  ctx.beginPath();
  ctx.arc(headDx * scale, headY, headR, 0, Math.PI * 2);
  ctx.fill();
  if (s.hitFlash > 0.01) {
    ctx.globalAlpha = s.hitFlash * 0.75;
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(headDx * scale, headY, headR * 1.12, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  // --- Arms -----------------------------------------------------------------
  // Guard rest poses. Glove height follows the AI's carried guard, so fatigue
  // and the punish window are both legible without any HUD.
  const restFor = (dir: number, guardVal: number) => ({
    fist: { x: (dir * 34 + headDx * 0.6) * scale, y: headY + (1 - guardVal) * 62 * scale + 10 * scale },
    elbow: { x: (dir * 48 + bodyDx * 0.5) * scale, y: shoulderY + 54 * scale },
  });

  const restL = restFor(-1, ai.guard.left);
  const restR = restFor(1, ai.guard.right);
  const shoulderL = { x: -34 * scale + bodyDx * scale, y: shoulderY + 12 * scale };
  const shoulderR = { x: 34 * scale + bodyDx * scale, y: shoulderY + 12 * scale };

  let poseL = { ...restL, gloveScale: 1, strikeT: 0 };
  let poseR = { ...restR, gloveScale: 1, strikeT: 0 };

  if (tel) {
    const dir = tel.side === 'left' ? -1 : 1;
    const targetY = tel.target === 'head' ? headY : shoulderY + torsoH * 0.45;
    const keysRaw = armKeys(tel.type, dir, geom, targetY);
    // Rig-local x values are authored unscaled; y values are already absolute.
    const k: ArmKey = {
      wind: { elbow: sx(keysRaw.wind.elbow, scale), fist: sx(keysRaw.wind.fist, scale) },
      strike: { elbow: sx(keysRaw.strike.elbow, scale), fist: sx(keysRaw.strike.fist, scale) },
      control: sx(keysRaw.control, scale),
      gloveScale: keysRaw.gloveScale,
    };
    const rest = tel.side === 'left' ? restL : restR;
    const pose = armAt(tel.progress, k, rest);

    // Motion trail, sampled from the same pure function at earlier progress.
    if (pose.strikeT > 0.05 && colour) {
      for (const back of [0.16, 0.3, 0.44]) {
        const tp = tel.progress - back * (1 - HOLD_END);
        if (tp <= HOLD_END) continue;
        const ghost = armAt(tp, k, rest);
        ctx.globalAlpha = 0.3 * (1 - back);
        ctx.fillStyle = colour;
        ctx.beginPath();
        ctx.arc(ghost.fist.x, ghost.fist.y, 21 * scale * ghost.gloveScale * 0.8, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }

    if (tel.side === 'left') poseL = pose;
    else poseR = pose;
  }

  const activeSide = tel ? tel.side : null;
  drawArm(ctx, shoulderL, poseL.elbow, poseL.fist, scale, poseL.gloveScale, activeSide === 'left' ? colour : null);
  drawArm(ctx, shoulderR, poseR.elbow, poseR.fist, scale, poseR.gloveScale, activeSide === 'right' ? colour : null);

  ctx.restore();
}

function sx(p: Pt, scale: number): Pt {
  return { x: p.x * scale, y: p.y };
}

/**
 * Shoulder -> elbow -> fist. The elbow is the whole point: it is what separates
 * a hook (high and wide) from an uppercut (low and tucked) from a straight
 * (extending along the line) long before the fist arrives.
 */
function drawArm(
  ctx: CanvasRenderingContext2D,
  shoulder: Pt,
  elbow: Pt,
  fist: Pt,
  scale: number,
  gloveScale: number,
  colour: string | null,
): void {
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Charging glow along the arm while a punch is loaded.
  if (colour) {
    ctx.globalAlpha = 0.28;
    ctx.strokeStyle = colour;
    ctx.lineWidth = 26 * scale;
    ctx.beginPath();
    ctx.moveTo(shoulder.x, shoulder.y);
    ctx.lineTo(elbow.x, elbow.y);
    ctx.lineTo(fist.x, fist.y);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  ctx.strokeStyle = colour ? '#3c4a60' : '#2a3446';
  ctx.lineWidth = 16 * scale;
  ctx.beginPath();
  ctx.moveTo(shoulder.x, shoulder.y);
  ctx.lineTo(elbow.x, elbow.y);
  ctx.stroke();

  ctx.strokeStyle = colour ? '#4a5a74' : '#334054';
  ctx.lineWidth = 13 * scale;
  ctx.beginPath();
  ctx.moveTo(elbow.x, elbow.y);
  ctx.lineTo(fist.x, fist.y);
  ctx.stroke();

  // Elbow joint, drawn explicitly so its position reads at a glance.
  ctx.fillStyle = colour ?? '#3a4760';
  ctx.beginPath();
  ctx.arc(elbow.x, elbow.y, 9 * scale, 0, Math.PI * 2);
  ctx.fill();

  const r = 21 * scale * gloveScale;
  if (colour) {
    ctx.globalAlpha = 0.32;
    ctx.fillStyle = colour;
    ctx.beginPath();
    ctx.arc(fist.x, fist.y, r * 1.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }
  ctx.beginPath();
  ctx.arc(fist.x, fist.y, r, 0, Math.PI * 2);
  ctx.fillStyle = colour ? GLOVE : GLOVE_DARK;
  ctx.fill();
  ctx.lineWidth = Math.max(1, r * 0.13);
  ctx.strokeStyle = colour ?? 'rgba(0,0,0,0.35)';
  ctx.stroke();
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

export { PUNCH_COLOUR };
